import React from 'react';
import {useRetrieveAccountLazyQueryRaw} from '@stripe-internal/ark';
import type {OnboardingStateValues, IOnboardingState} from './OnboardingState';
import {useWarnOnChangingParameterAfterInitialRender} from '../../utils/parameters/useWarnOnChangingSupplementalObject';
import type {ConnectElementImportKeys} from '../../connect/ConnectJSInterface/ConnectElementList';

type Props = {
  onboardingStateObserver: IOnboardingState;
  connectElement: ConnectElementImportKeys;
};

type IOnboardingContext = {
  isOnboardingComplete: boolean;
  detailsSubmitted: boolean;
  handleOnboardingComplete: () => Promise<void>;
};

const defaultOnboardingContext: IOnboardingContext = {
  isOnboardingComplete: false,
  detailsSubmitted: false,
  handleOnboardingComplete: async () => {},
};

export const OnboardingContext = React.createContext<IOnboardingContext>(
  defaultOnboardingContext,
);
OnboardingContext.displayName = 'OnboardingContext';

export const useOnboardingContext = () => React.useContext(OnboardingContext);

export const OnboardingProvider: React.FC<Props> = ({
  children,
  onboardingStateObserver,
  connectElement,
}) => {
  const [retrieveAccount] = useRetrieveAccountLazyQueryRaw({
    fetchPolicy: 'no-cache',
  });
  const [onboardingState, setOnboardingState] =
    React.useState<OnboardingStateValues>(onboardingStateObserver.values);
  useWarnOnChangingParameterAfterInitialRender(
    'collectionOptions.fields',
    (obj) => obj.collectionOptions?.fields,
    connectElement,
  );
  useWarnOnChangingParameterAfterInitialRender(
    'collectionOptions.futureRequirements',
    (obj) => obj.collectionOptions?.futureRequirements,
    connectElement,
  );

  React.useEffect(() => {
    const observer = () => {
      setOnboardingState(onboardingStateObserver.values);
    };
    onboardingStateObserver.registerObserver(observer);
    return () => {
      onboardingStateObserver.unregisterObserver(observer);
    };
  }, [onboardingStateObserver]);

  const handleOnboardingComplete = async () => {
    const retrievedAccount = await retrieveAccount();
    const detailsSubmitted =
      retrievedAccount.data?.v1Account?.details_submitted;
    onboardingStateObserver.updateValues({
      isOnboardingComplete: true,
      ...(detailsSubmitted !== undefined && {
        detailsSubmitted,
      }),
    });
  };

  return (
    <OnboardingContext.Provider
      value={{
        isOnboardingComplete: onboardingState.isOnboardingComplete,
        detailsSubmitted: onboardingState.detailsSubmitted,
        handleOnboardingComplete,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};
